import { Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import type { SurveyPreset, SurveyStatus } from '@citizen-walk/shared-types';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSurveyPresets } from '../masters';
import { SurveyImportFromPresetModal } from './SurveyImportFromPresetModal';
import {
  buildSurveyExport,
  buildSurveyExportFilename,
  downloadSurveyJson,
  parseSurveyExport,
} from './surveyJsonIo';
import { copyPresetToDistrict, linkPresetToDistrict } from './useSurveyCopyLink';
import {
  createSurvey,
  duplicateSurvey,
  importSurveyFromExport,
  refreshLinkedSurvey,
  setSurveyStatus,
  useSurveys,
  type SurveyListItem,
} from './useSurveys';

/**
 * アンケート一覧画面 (admin 第 22 弾、Phase 5 A-S13)。
 *
 * 旧 `old/apps/admin/src/pages/SurveysPage.tsx` (一覧 + 作成ボタン) を v2 callable 不要・
 * Firestore 直接 CRUD で踏襲。本 sprint scope は minimum (master 連携 / duplicate / copy URL は
 * 別 sprint = GAP-A9)。
 *
 * 認可: master / district admin (担当地区) のみ操作可。rules で deny される (UI でも guard)。
 */

const STATUS_LABEL: Record<SurveyStatus, string> = {
  draft: '下書き',
  published: '配信中',
  closed: '締切',
};

const STATUS_BADGE: Record<SurveyStatus, string> = {
  draft: 'bg-slate-300 text-slate-800',
  published: 'bg-success-600 text-white',
  closed: 'bg-slate-500 text-white',
};

/** quick toggle 用: draft ↔ published ↔ closed を循環。 */
function nextStatus(s: SurveyStatus): SurveyStatus {
  if (s === 'draft') return 'published';
  if (s === 'published') return 'closed';
  return 'draft';
}

export function SurveysAdminPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const navigate = useNavigate();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const surveysState = useSurveys(districtId ?? null);
  const presetsState = useSurveyPresets();
  const { toast } = useToast();

  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Sprint BG-DataSettings 12 — Master preset 取込 modal の open/busy 状態
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  // AW8 (docs/108) — JSON import (file) + 再リンク / アーカイブ確認ダイアログ
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [jsonBusy, setJsonBusy] = useState(false);
  const [relinkTarget, setRelinkTarget] = useState<SurveyListItem | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<SurveyListItem | null>(null);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const uid = authState.user?.uid ?? '';

  async function handleCreate() {
    if (!districtId || !canManage) return;
    setCreating(true);
    try {
      const newId = await createSurvey(districtId, uid);
      toast('success', 'アンケートを作成しました');
      navigate(`/districts/${districtId}/surveys/${newId}`);
    } catch (e) {
      toast('error', `作成失敗: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  }

  async function handleToggleStatus(item: SurveyListItem) {
    if (!districtId || !canManage) return;
    const next = nextStatus(item.status);
    setBusyId(item.id);
    try {
      await setSurveyStatus(districtId, item.id, next, uid, item.version);
      toast('success', `「${item.title || '(無題)'}」を ${STATUS_LABEL[next]} にしました`);
    } catch (e) {
      toast('error', `状態変更失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  /** GAP-A9 (Sprint BE) — survey を複製。draft 新規作成のあと編集ページに遷移。 */
  async function handleDuplicate(item: SurveyListItem) {
    if (!districtId || !canManage) return;
    setBusyId(item.id);
    try {
      const newId = await duplicateSurvey(districtId, item, uid);
      toast('success', '複製しました。draft として保存されています。');
      navigate(`/districts/${districtId}/surveys/${newId}`);
    } catch (e) {
      toast('error', `複製失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  /** GAP-A9 (Sprint BE) — 参加者向け deep link を clipboard にコピー。 */
  async function handleCopyLink(item: SurveyListItem) {
    if (!districtId) return;
    const urlToken = districtState.status === 'ready' ? districtState.district.urlToken : null;
    if (!urlToken) {
      toast('error', '地区情報を読込中のため、しばらく待ってから再試行してください。');
      return;
    }
    const url = `${window.location.origin}/m/${urlToken}/surveys/${item.id}`;
    try {
      // navigator.clipboard は HTTPS / localhost のみで動作。fallback: prompt
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // eslint-disable-next-line no-alert
        window.prompt('回答リンク (Ctrl+C でコピー):', url);
      }
      toast('success', '回答リンクをコピーしました');
    } catch (e) {
      toast('error', `コピー失敗: ${(e as Error).message}`);
    }
  }

  /** GAP-A10 (Sprint BE) — 回答 (CSV/JSON) page に遷移。 */
  function handleViewResponses(item: SurveyListItem) {
    if (!districtId) return;
    navigate(`/districts/${districtId}/surveys/${item.id}/responses`);
  }

  /** AW8 (docs/108 High3) — アンケート単位 JSON export (ファイル DL)。 */
  function handleExportJson(item: SurveyListItem) {
    const districtName = districtState.status === 'ready' ? districtState.district.name : undefined;
    const exp = buildSurveyExport(item, districtName);
    downloadSurveyJson(buildSurveyExportFilename(item), exp);
  }

  /** AW8 (docs/108 High3) — JSON ファイルから新規アンケート取込 (draft)。 */
  async function handleImportFile(file: File) {
    if (!districtId || !canManage) return;
    setJsonBusy(true);
    try {
      const text = await file.text();
      const exp = parseSurveyExport(text);
      const newId = await importSurveyFromExport(districtId, exp, uid);
      toast('success', 'インポートしました (draft として作成)');
      navigate(`/districts/${districtId}/surveys/${newId}`);
    } catch (e) {
      toast('error', `インポート失敗: ${(e as Error).message}`);
    } finally {
      setJsonBusy(false);
    }
  }

  /** AW8 (docs/108 High2) — linked アンケートを master 最新で再リンク。 */
  async function handleRefreshLinked(item: SurveyListItem) {
    if (!districtId || !canManage) return;
    setBusyId(item.id);
    try {
      await refreshLinkedSurvey(districtId, item, uid);
      toast('success', 'マスターテンプレの最新内容で再リンクしました');
    } catch (e) {
      toast('error', `再リンク失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
      setRelinkTarget(null);
    }
  }

  /**
   * AW8 (docs/108 Medium) — アンケートのアーカイブ。
   * firestore.rules は物理 delete 禁止 (回答履歴保護) なので v2 archive 方針に従い
   * status='closed' で運用停止 (旧2 の物理 delete を archive で代替)。
   */
  async function handleArchive(item: SurveyListItem) {
    if (!districtId || !canManage) return;
    setBusyId(item.id);
    try {
      await setSurveyStatus(districtId, item.id, 'closed', uid, item.version);
      toast('success', `「${item.title || '(無題)'}」を締切 (アーカイブ) にしました`);
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
      setArchiveTarget(null);
    }
  }

  /** Sprint BG-DataSettings 12 — Master preset から copy で取込。 */
  async function handleCopyFromPreset(preset: SurveyPreset & { id: string }) {
    if (!districtId || !canManage) return;
    setImportBusy(true);
    try {
      const newId = await copyPresetToDistrict(districtId, preset, uid);
      toast('success', `「${preset.displayName}」を copy で取込みました。draft として保存。`);
      setImportModalOpen(false);
      navigate(`/districts/${districtId}/surveys/${newId}`);
    } catch (e) {
      toast('error', `copy 取込失敗: ${(e as Error).message}`);
    } finally {
      setImportBusy(false);
    }
  }

  /** Sprint BG-DataSettings 12 — Master preset から link で取込。 */
  async function handleLinkFromPreset(preset: SurveyPreset & { id: string }) {
    if (!districtId || !canManage) return;
    setImportBusy(true);
    try {
      const newId = await linkPresetToDistrict(districtId, preset, uid);
      toast('success', `「${preset.displayName}」を link で取込みました。draft として保存。`);
      setImportModalOpen(false);
      navigate(`/districts/${districtId}/surveys/${newId}`);
    } catch (e) {
      toast('error', `link 取込失敗: ${(e as Error).message}`);
    } finally {
      setImportBusy(false);
    }
  }

  const items = surveysState.status === 'ready' ? surveysState.items : [];

  // Sprint BG-Menu (第 2 段): list item に「テンプレ: <preset name>」バッジ表示用に
  // sourceMasterSurveyId → preset displayName のマップを作る (preset 一覧 ready 時のみ)。
  const presetNameById = (() => {
    if (presetsState.status !== 'ready') return new Map<string, string>();
    return new Map(presetsState.items.map((p) => [p.id, p.displayName]));
  })();

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          運用中のアンケート
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.2.8〜10 — 地区別アンケートの設計 / 取込 (テンプレ・JSON) / 複製 / 再リンク /
          回答閲覧・エクスポート / アーカイブ。
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区のアンケート管理権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      <Card padding="md" className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm text-slate-700">
          テンプレ (アンケートテンプレ) から取込んで運用、または空の新規アンケートを作成します。
        </span>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="primary"
            onClick={() => setImportModalOpen(true)}
            disabled={!canManage || creating || importBusy}
            title="アンケートテンプレから copy / link で取込"
          >
            📥 テンプレから作成
          </Button>
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canManage || jsonBusy}
            title="JSON ファイルからアンケートを取込 (draft 作成)"
          >
            {jsonBusy ? '取込中…' : '⬆ JSON インポート'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleImportFile(f);
              e.target.value = '';
            }}
          />
          <Button
            variant="secondary"
            onClick={() => void handleCreate()}
            disabled={!canManage || creating}
            title="空のアンケートを新規作成 (テンプレ無し)"
          >
            {creating ? '作成中…' : '＋ 空から新規作成'}
          </Button>
        </div>
      </Card>

      {importModalOpen && (
        <SurveyImportFromPresetModal
          presets={presetsState.status === 'ready' ? presetsState.items : []}
          loading={presetsState.status === 'loading'}
          busy={importBusy}
          onCopy={(preset) => void handleCopyFromPreset(preset)}
          onLink={(preset) => void handleLinkFromPreset(preset)}
          onClose={() => setImportModalOpen(false)}
        />
      )}

      <h2 className="text-base font-bold text-slate-900 mb-3">アンケート一覧</h2>

      {surveysState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {surveysState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {surveysState.error.message}</p>
        </Card>
      )}

      {surveysState.status === 'ready' && items.length === 0 && (
        <Card padding="md">
          <p className="text-sm text-slate-600">まだアンケートはありません。</p>
        </Card>
      )}

      {surveysState.status === 'ready' && items.length > 0 && (
        <ul className="grid grid-cols-1 gap-2">
          {items.map((s) => (
            <li key={s.id}>
              <Card padding="md" className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE[s.status]}`}>
                      {STATUS_LABEL[s.status]}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                      {s.questions.length} 問
                    </span>
                    {typeof s.responseCount === 'number' && s.responseCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
                        回答 {s.responseCount}
                      </span>
                    )}
                    {s.sourceMasterSurveyId && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-warning-100 text-warning-700 border border-warning-300"
                        title={`元テンプレ: ${s.sourceMasterSurveyId}`}
                      >
                        テンプレ:{' '}
                        {presetNameById.get(s.sourceMasterSurveyId) ?? s.sourceMasterSurveyId}
                      </span>
                    )}
                    {s.linkedToMaster === 'linked' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
                        🔗 リンク (master v{s.masterVersion ?? '?'})
                      </span>
                    )}
                    {s.linkedToMaster === 'forked' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                        📋 コピー由来
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm font-bold text-slate-900 truncate">
                    {s.title || '(無題)'}
                  </p>
                  {s.description && (
                    <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                      {s.description}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-400">
                    ID: <code>{s.id}</code> / 更新: {new Date(s.updatedAt).toLocaleString('ja-JP')}
                  </p>
                </div>
                {canManage && (
                  <div className="shrink-0 flex flex-col gap-1">
                    <Link
                      to={`/districts/${districtId}/surveys/${s.id}`}
                      className="text-xs text-primary-600 hover:text-primary-700 hover:underline text-center min-h-touch flex items-center justify-center px-3"
                    >
                      編集 →
                    </Link>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleToggleStatus(s)}
                      disabled={busyId === s.id}
                      title={`次の状態: ${STATUS_LABEL[nextStatus(s.status)]}`}
                    >
                      → {STATUS_LABEL[nextStatus(s.status)]}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleDuplicate(s)}
                      disabled={busyId === s.id}
                      title="このアンケートを複製 (新規 draft として保存)"
                    >
                      📋 複製
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleCopyLink(s)}
                      disabled={busyId === s.id}
                      title="参加者向け回答リンクをクリップボードにコピー"
                    >
                      🔗 リンク取得
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleViewResponses(s)}
                      disabled={busyId === s.id}
                      title="回答一覧 (CSV / JSON エクスポート)"
                    >
                      📥 回答
                    </Button>
                    {s.linkedToMaster === 'linked' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setRelinkTarget(s)}
                        disabled={busyId === s.id}
                        title="マスターテンプレの最新内容で上書き再同期"
                      >
                        🔄 再リンク
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleExportJson(s)}
                      disabled={busyId === s.id}
                      title="このアンケートを JSON でエクスポート"
                    >
                      ⬇ JSON
                    </Button>
                    {s.status !== 'closed' && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setArchiveTarget(s)}
                        disabled={busyId === s.id}
                        title="アーカイブ (status=closed、物理削除は不可)"
                      >
                        🗑 アーカイブ
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={relinkTarget !== null}
        intent="warning"
        title="マスターテンプレで再リンク"
        message={
          relinkTarget
            ? `「${relinkTarget.title || '(無題)'}」を元テンプレの最新内容で上書きします。\n` +
              '地区側で加えた質問の編集内容は失われます。よろしいですか?'
            : ''
        }
        confirmLabel="再リンクする"
        onConfirm={() => {
          if (relinkTarget) void handleRefreshLinked(relinkTarget);
        }}
        onCancel={() => setRelinkTarget(null)}
      />

      <ConfirmDialog
        open={archiveTarget !== null}
        intent="danger"
        title="アンケートをアーカイブ"
        message={
          archiveTarget
            ? `「${archiveTarget.title || '(無題)'}」を締切 (アーカイブ) にします。\n` +
              ((archiveTarget.responseCount ?? 0) > 0
                ? `※ 既に ${archiveTarget.responseCount} 件の回答があります。回答データは保持されます (物理削除は不可)。\n`
                : '') +
              '参加者には表示されなくなります。よろしいですか?'
            : ''
        }
        confirmLabel="アーカイブする"
        onConfirm={() => {
          if (archiveTarget) void handleArchive(archiveTarget);
        }}
        onCancel={() => setArchiveTarget(null)}
      />
    </>
  );
}
